import { useCallback, useEffect, useRef, useState } from 'react';
import { createSession, closeSession } from '../api/orchestration';
import { useSseStream } from './useSseStream';
import { extractContent, extractContentBlocks, extractHaltOptions, extractOrderInsight } from './sseReducer';
import { extractErrorMessage } from '../services/archerClient';
import type {
  ChatMessage,
  Citation,
  ContentBlock,
  HaltPayload,
  InteractionType,
  OrderInsightPayload,
  ToolCallEventData,
} from '../interfaces/chat';

const HISTORY_STORAGE_PREFIX = 'archer-chat:history:';

function loadStoredMessages(sessionId: string): ChatMessage[] {
  try {
    const raw = localStorage.getItem(HISTORY_STORAGE_PREFIX + sessionId);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ChatMessage[];
    return parsed.map((m) => ({ ...m, timestamp: new Date(m.timestamp) }));
  } catch {
    return [];
  }
}

function persistMessages(sessionId: string, messages: ChatMessage[]): void {
  try {
    localStorage.setItem(HISTORY_STORAGE_PREFIX + sessionId, JSON.stringify(messages));
  } catch {
    /* best effort — e.g. storage full or unavailable */
  }
}

function clearStoredMessages(sessionId: string): void {
  try {
    localStorage.removeItem(HISTORY_STORAGE_PREFIX + sessionId);
  } catch {
    /* best effort */
  }
}

/**
 * Top-level chat orchestration: owns the session lifecycle and reduces the SSE
 * event stream into a `messages[]` model. Ported from the archer-ai example
 * client, adapted to surface friendly RFC 7807 error messages and to start a
 * session automatically once a chatbotId is available. Message history is
 * mirrored to localStorage keyed by sessionId so a resumed session (see
 * `startSession`'s `resumeSessionId` param) can redisplay prior messages.
 */
export function useChat(chatbotId: string | null, chatbotVersionId?: string) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { connectionState, sseEvents, isStreaming, streamError, connect, send, disconnect, clearEvents } =
    useSseStream(sessionId);

  const processedCountRef = useRef(0);
  const callbackIdRef = useRef<string | null>(null);
  // Buffer tool calls fired during the current turn so they attach to the
  // assistant message that follows (chat reads: user → response → tools footer).
  const pendingToolCallsRef = useRef<ToolCallEventData[]>([]);
  // At most one order_insight per assistant turn; buffered the same way as
  // tool calls so it attaches to the message it accompanies.
  const pendingOrderInsightRef = useRef<OrderInsightPayload | undefined>(undefined);

  useEffect(() => {
    if (sseEvents.length <= processedCountRef.current) return;

    const newEvents = sseEvents.slice(processedCountRef.current);
    processedCountRef.current = sseEvents.length;

    for (const event of newEvents) {
      if (!event.data || event.data === '[DONE]') {
        if (event.type === 'done') setStatusMessage(null);
        continue;
      }

      if (event.type === 'chat.status' || event.type === 'chat.tool_call') {
        try {
          const parsed = JSON.parse(event.data);
          if (parsed.description) setStatusMessage(parsed.description);
        } catch {
          /* skip */
        }
      }

      if (event.type === 'chat.tool_call') {
        try {
          const toolCall = JSON.parse(event.data) as ToolCallEventData;
          if (toolCall.toolCallId && toolCall.toolName) pendingToolCallsRef.current.push(toolCall);
        } catch {
          /* skip malformed payload */
        }
      }

      if (event.type === 'chat.order_insight') {
        try {
          const parsed = extractOrderInsight(JSON.parse(event.data));
          if (parsed) pendingOrderInsightRef.current = parsed;
        } catch {
          /* skip malformed payload */
        }
      }

      if (event.type === 'chat.error') {
        setStatusMessage(null);
        try {
          const parsed = JSON.parse(event.data);
          if (parsed?.detail || parsed?.message) {
            setError(String(parsed.detail || parsed.message));
          }
        } catch {
          /* skip */
        }
      }

      if (event.type === 'chat.completed' || event.type === 'chat.error') {
        setStatusMessage(null);
        if (event.type === 'chat.completed') {
          const drained = pendingToolCallsRef.current;
          pendingToolCallsRef.current = [];
          const orderInsight = pendingOrderInsightRef.current;
          pendingOrderInsightRef.current = undefined;
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (!last || last.role === 'user') {
              return [
                ...prev,
                {
                  id: crypto.randomUUID(),
                  role: 'assistant' as const,
                  content: '_The assistant did not produce a visible response._',
                  timestamp: new Date(),
                  toolCalls: drained.length > 0 ? drained : undefined,
                  orderInsight,
                },
              ];
            }
            if (last.role === 'assistant' && (drained.length > 0 || orderInsight)) {
              return [
                ...prev.slice(0, -1),
                {
                  ...last,
                  toolCalls: drained.length > 0 ? [...(last.toolCalls ?? []), ...drained] : last.toolCalls,
                  orderInsight: orderInsight ?? last.orderInsight,
                },
              ];
            }
            return prev;
          });
        }
      }

      if (event.type === 'chat.citation') {
        try {
          const citation = JSON.parse(event.data) as Citation;
          setMessages((prev) => {
            const lastIdx = prev.length - 1;
            const last = prev[lastIdx];
            if (!last || last.role !== 'assistant') return prev;
            const next = { ...last, citations: [...(last.citations ?? []), citation] };
            return [...prev.slice(0, lastIdx), next];
          });
        } catch {
          /* skip malformed citation */
        }
        continue;
      }

      if (event.type !== 'chat.block' && event.type !== 'chat.halted') continue;

      try {
        const parsed = JSON.parse(event.data);

        let halt: HaltPayload | undefined;
        if (event.type === 'chat.halted' && typeof parsed.callbackId === 'string') {
          callbackIdRef.current = parsed.callbackId;
          const haltType = typeof parsed.type === 'string' ? (parsed.type as InteractionType) : undefined;
          if (haltType) {
            halt = {
              callbackId: parsed.callbackId,
              type: haltType,
              options: extractHaltOptions(parsed.options),
              formDefinition: parsed.formDefinition ?? undefined,
            };
          }
        }

        const content = extractContent(parsed);
        const contentBlocks: ContentBlock[] | undefined = extractContentBlocks(parsed);
        if (content || halt || contentBlocks) {
          const drained = pendingToolCallsRef.current;
          pendingToolCallsRef.current = [];
          const orderInsight = pendingOrderInsightRef.current;
          pendingOrderInsightRef.current = undefined;
          setMessages((prev) => {
            const lastMsg = prev[prev.length - 1];
            if (lastMsg?.role === 'assistant') {
              return [
                ...prev.slice(0, -1),
                {
                  ...lastMsg,
                  content: lastMsg.content + (content ?? ''),
                  toolCalls:
                    drained.length > 0 ? [...(lastMsg.toolCalls ?? []), ...drained] : lastMsg.toolCalls,
                  halt: halt ?? lastMsg.halt,
                  orderInsight: orderInsight ?? lastMsg.orderInsight,
                  contentBlocks: contentBlocks ?? lastMsg.contentBlocks,
                },
              ];
            }
            return [
              ...prev,
              {
                id: crypto.randomUUID(),
                role: 'assistant',
                content: content ?? '',
                timestamp: new Date(),
                toolCalls: drained.length > 0 ? drained : undefined,
                halt,
                orderInsight,
                contentBlocks,
              },
            ];
          });
        }
      } catch {
        /* non-JSON event data, skip */
      }
    }
  }, [sseEvents]);

  const startSession = useCallback(
    async (resumeSessionId?: string): Promise<string | null> => {
      if (!chatbotId) return null;
      try {
        setError(null);
        let id: string;
        if (resumeSessionId) {
          id = resumeSessionId;
          setSessionId(id);
          setMessages(loadStoredMessages(id));
        } else {
          id = await createSession(chatbotId, chatbotVersionId);
          setSessionId(id);
          setMessages([]);
        }
        clearEvents();
        processedCountRef.current = 0;
        callbackIdRef.current = null;
        pendingToolCallsRef.current = [];
        pendingOrderInsightRef.current = undefined;
        return id;
      } catch (err) {
        setError(extractErrorMessage(err, 'Failed to start the assistant session.'));
        return null;
      }
    },
    [chatbotId, chatbotVersionId, clearEvents],
  );

  // Mirror messages to localStorage as they change so a session can be
  // resumed (by sessionId) after the iframe is torn down and recreated.
  useEffect(() => {
    if (!sessionId) return;
    persistMessages(sessionId, messages);
  }, [sessionId, messages]);

  const endSession = useCallback(async () => {
    const id = sessionId;
    if (!id) return;
    setSessionId(null);
    setMessages([]);
    clearEvents();
    processedCountRef.current = 0;
    callbackIdRef.current = null;
    pendingToolCallsRef.current = [];
    pendingOrderInsightRef.current = undefined;
    disconnect();
    clearStoredMessages(id);
    try {
      await closeSession(id);
    } catch {
      /* best effort */
    }
  }, [sessionId, disconnect, clearEvents]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!sessionId) return;
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: 'user', content: text, timestamp: new Date() },
      ]);
      setStatusMessage(null);
      setError(null);
      try {
        if (connectionState.status === 'disconnected' || connectionState.status === 'error') {
          connect();
        }
        const cbId = callbackIdRef.current;
        callbackIdRef.current = null;
        await send(text, cbId);
      } catch (err) {
        setError(extractErrorMessage(err, 'Failed to send your message.'));
      }
    },
    [sessionId, send, connect, connectionState.status],
  );

  // Reconnect the SSE stream whenever we have a session but the transport dropped.
  useEffect(() => {
    if (sessionId && connectionState.status === 'disconnected') connect();
  }, [sessionId, connectionState.status, connect]);

  // Surface a per-turn response timeout (e.g. the pod processing the turn
  // died mid-flight) as a regular error so the user can see it and retry.
  useEffect(() => {
    if (streamError) setError(streamError);
  }, [streamError]);

  // Dev-only escape hatch for visually validating the order-insight components
  // against a realistic payload without a live backend contract (see
  // src/fixtures/orderInsight.fixtures.ts). Never present in production
  // builds. From a dev-build console: `window.__injectOrderInsight(fixture)`.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const inject = (payload: OrderInsightPayload) => {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: `_[dev fixture] order_insight for ${payload.orderId}_`,
          timestamp: new Date(),
          orderInsight: payload,
        },
      ]);
    };
    (window as unknown as { __injectOrderInsight?: typeof inject }).__injectOrderInsight = inject;
    return () => {
      delete (window as unknown as { __injectOrderInsight?: typeof inject }).__injectOrderInsight;
    };
  }, []);

  return {
    sessionId,
    messages,
    connectionState,
    isStreaming,
    statusMessage,
    error,
    setError,
    startSession,
    endSession,
    sendMessage,
  };
}
